import { Component, Input, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatRadioChange } from "@angular/material/radio";
import { BehaviorSubject, combineLatest, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { EditOptionDialogComponent } from "./edit-option-dialog.component";
import { AddScopeDialogComponent } from "./add-scope-dialog.component";
import { NatureAndExtentOfValuersWorkService } from "./nature-and-extent-of-valuers-work.service";
import { ReportTypes, WorkScope, WorkScopeOption } from "./types";
import { FormControl, FormGroup } from "@angular/forms";
import { formControlValueChanges } from "src/app/core/v2/utils";

@Component({
  selector: 'app-nature-and-extent-of-valuers-work',
  templateUrl: './nature-and-extent-of-valuers-work.component.html',
  styleUrls: ['./nature-and-extent-of-valuers-work.component.scss']
})
export class NatureAndExtentOfValuersWorkComponent implements OnDestroy { 
  @Input()
  set agencyId(value: number) {
    if (!value) return;
    this._fetchWorkScopes(value)
  }

  form = new FormGroup({
    report_type_id: new FormControl<number|null>(ReportTypes.Default, [])
  })
  reportTypes = [
    {value: ReportTypes.Default, label: 'Interval Default'},
    {value: ReportTypes.Bank, label: 'Bank Report'},
    {value: ReportTypes.Commercial, label: 'Commercial Report'},
    {value: ReportTypes.FairValue, label: 'Fair Value Report'}
  ]

  private _isLoading$ = new BehaviorSubject<boolean>(true)
  private _workScopes$ = new BehaviorSubject<WorkScope[]>([])
  private _onDestory$ = new Subject<void>()

  workScopes$ = combineLatest([
    this._workScopes$.asObservable(),
    formControlValueChanges(this.form.controls.report_type_id)
  ]).pipe(map(([scopes, reportTypeId]) => {
    return scopes.filter(scope => scope.reportTypeId === reportTypeId)
  }))
  isLoading$ = this._isLoading$.asObservable()

  constructor(
    private _dialog: MatDialog,
    private _service: NatureAndExtentOfValuersWorkService
  ) {}

  ngOnDestroy(): void {
    this._onDestory$.next()
    this._onDestory$.complete()
  }

  onAddScope() {
    const dialogRef = this._dialog.open(AddScopeDialogComponent, {
      width: '400px',
      data: {
        reportTypeId: this.form.controls.report_type_id.value
      }
    })

    dialogRef.afterClosed().subscribe(res => {
      if (!res) return;

      const scopes = this._workScopes$.getValue()
      this._workScopes$.next([...scopes, res])
    })
  }

  onAddOption(scopeOfWorkId: number | string) {
    const dialogRef = this._dialog.open(EditOptionDialogComponent, {
      width: '400px',
    })
    dialogRef.afterClosed().subscribe(res => {
      if (!res) return;

      const scopes = this._workScopes$.getValue()
      this._workScopes$.next(scopes.map(item => {
        if (item.id !== scopeOfWorkId) return item
        return {
          ...item,
          options: [...item.options, res]
        }
      }))
    })
  }

  onEditOption(scopeOfWorkId: number | string, option: WorkScopeOption) {
    const dialogRef = this._dialog.open(EditOptionDialogComponent, {
      width: '400px',
      data: {
        option
      }
    })
    dialogRef.afterClosed().subscribe(res => {
      if (!res) return;

      const scopes = this._workScopes$.getValue()
      this._workScopes$.next(scopes.map(item => {
        if (item.id !== scopeOfWorkId) return item
        return {
          ...item,
          options: item.options.map(_option => {
            if (_option.id !== option.id) return _option
            return {...res}
          })
        }
      }))
    })
  }

  onResetOption(scopeOfWorkId: number | string, option: WorkScopeOption) {
    const scopes = this._workScopes$.getValue()
    this._workScopes$.next(scopes.map(item => {
      if (item.id !== scopeOfWorkId) return item
      return {
        ...item,
        options: item.options.map(_option => {
          if (_option.id !== option.id) return _option
          return {
            ..._option,
            name: _option.defaultName,
            description: _option.defaultDescription
          }
        })
      }
    }))
  }

  onDeleteOption(scopeOfWorkId: number | string, option: WorkScopeOption) {
    const scopes = this._workScopes$.getValue()
    this._workScopes$.next(scopes.map(item => {
      if (item.id !== scopeOfWorkId) return item
      return {
        ...item,
        options: item.options.filter(_option => {
          if (_option.id !== option.id) return true
          return false
        })
      }
    }))
  }

  onScopeOfWorkOptionChange(scopeOfWorkId: number | string, change: MatRadioChange) {
    const scopes = this._workScopes$.getValue()
    this._workScopes$.next(scopes.map(item => {
      if (item.id !== scopeOfWorkId) return item
      return {
        ...item,
        selectedOptionId: change.value
      }
    }))
  }

  private _fetchWorkScopes(agencyId: number) {
    this._service.fetchList(agencyId).pipe(
      takeUntil(this._onDestory$)
    ).subscribe(items => {
      this._isLoading$.next(false)
      this._workScopes$.next(items)
    })
  }

  getData() {
    const scopes = this._workScopes$.getValue()
    return scopes
  }
}